import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CalendarDay {
  date: Dayjs;
  dayOfMonth: number;
  month: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

const useCalendarDays = (currentDate = dayjs()) => {
  const [days, setDays] = useState<CalendarDay[]>([]);
  const { i18n } = useTranslation();

  useEffect(() => {
    const currentLocale = i18n.language.toLowerCase();
    dayjs.locale(currentLocale);

    const firstDayOfMonth = currentDate.startOf('month');
    const lastDayOfMonth = currentDate.endOf('month');

    const firstDayOfWeek = dayjs().localeData().firstDayOfWeek();

    let firstDayToDisplay = firstDayOfMonth.startOf('week');
    if (firstDayOfWeek !== 0) {
      const diff = firstDayOfMonth.day() - firstDayOfWeek;
      firstDayToDisplay = firstDayOfMonth.subtract(diff < 0 ? diff + 7 : diff, 'day');
    }

    let lastDayToDisplay = lastDayOfMonth.endOf('week');
    if (firstDayOfWeek !== 0) {
      const diff = lastDayOfMonth.day() - firstDayOfWeek;
      lastDayToDisplay = lastDayOfMonth.add(6 - (diff < 0 ? diff + 7 : diff), 'day');
    }

    const calendarDays = [];
    let day = firstDayToDisplay;

    while (day.isBefore(lastDayToDisplay) || day.isSame(lastDayToDisplay, 'day')) {
      calendarDays.push({
        date: day,
        dayOfMonth: day.date(),
        month: day.month(),
        isCurrentMonth: day.month() === currentDate.month(),
        isToday: day.isSame(dayjs(), 'day'),
      });
      day = day.add(1, 'day');
    }

    setDays(calendarDays);
  }, [currentDate.format('YYYY-MM-DD'), i18n.language]);

  return days;
};

export default useCalendarDays;
